// Wall-clock `local` ("YYYY-MM-DDTHH:mm") in IANA zone `tz` -> Date (UTC instant), or null if that time does not
// exist (spring-forward gap). For ambiguous times (clocks going back) the EARLIER instant wins, like the server
// (Python fold=0). Preview only; the server decides.
export function zonedTimeToUtc(local, tz) {
  const [d, t] = (local || '').split('T');
  if (!d || !t) return null;
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  if ([y, mo, da, h, mi].some((n) => Number.isNaN(n))) return null;
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  const wall = (ts) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit',
      day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(ts));
    const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour, +v.minute, +v.second);
  };
  const DAY = 86400000;
  // Offsets either side of the guess cover any DST transition near it.
  const offsets = new Set([guess - DAY, guess, guess + DAY].map((g) => wall(g) - g));
  const matches = [...offsets].map((o) => guess - o).filter((ts) => wall(ts) === guess);
  if (!matches.length) return null;
  return new Date(Math.min(...matches));
}

export function relative(date, now = new Date()) {
  const mins = Math.round((date - now) / 60000);
  if (mins === 0) return 'just now';
  const abs = Math.abs(mins);
  const [n, unit] = abs < 60 ? [abs, 'minute'] : abs < 2880 ? [Math.round(abs / 60), 'hour'] : [Math.round(abs / 1440), 'day'];
  const s = `${n} ${unit}${n === 1 ? '' : 's'}`;
  return mins >= 0 ? `in ${s}` : `${s} ago`;
}

export function scheduleText(schedule) {
  if (!schedule) return '';
  const runAt = new Date(schedule.runAt);
  const local = new Intl.DateTimeFormat('en-GB', { timeZone: schedule.timeZone, weekday: 'short', day: 'numeric',
    month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(runAt);
  const utc = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', hour: '2-digit',
    minute: '2-digit', hourCycle: 'h23' }).format(runAt);
  return `${local} ${schedule.timeZone} · ${utc} UTC · ${relative(runAt)}`;
}

export function timeZones() {
  try { return Intl.supportedValuesOf('timeZone'); } catch { return ['UTC', 'Asia/Tokyo', 'Asia/Karachi', 'Europe/London', 'America/New_York']; }
}

export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
